import { type FC, Fragment, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  alpha,
  Chip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  PagePaths,
  type MenuSectionProps,
  type MenuItemProps,
} from "@/partner/shared/constants";

export interface MultiLevelMenuProps {
  items: (MenuItemProps | MenuSectionProps)[];
  /** Счётчики у пунктов по item.to; ноль и отсутствие не рисуются. */
  badges?: Record<string, number>;
}

const PARTNER_SIDEBAR_EXPANDED_KEY = "partner-sidebar-expanded";

// Дашборд подсвечивается только на точном совпадении, иначе он был бы
// активен на любой странице кабинета (все пути начинаются с "/partner").
const isPathActive = (to: string, pathname: string) =>
  to === PagePaths.partner ? pathname === to : pathname.startsWith(to);

const badge = (count: number | undefined) =>
  count && count > 0 ? (
    <Chip size="small" color="error" label={count} sx={{ ml: 1 }} />
  ) : null;

export const MultiLevelMenu: FC<MultiLevelMenuProps> = ({
  items,
  badges = {},
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const getInitialExpanded = () => {
    const saved = localStorage.getItem(PARTNER_SIDEBAR_EXPANDED_KEY);
    let initial: Record<string, boolean> = {};
    if (saved) {
      try {
        initial = JSON.parse(saved);
      } catch {
        localStorage.removeItem(PARTNER_SIDEBAR_EXPANDED_KEY);
      }
    }
    // Секция, содержащая активный маршрут, раскрывается автоматически.
    items.forEach((item) => {
      if (
        item.section &&
        item.items.some((subItem) =>
          isPathActive(subItem.to, location.pathname)
        )
      ) {
        initial[item.section] = true;
      }
    });
    return initial;
  };

  const [expanded, setExpanded] =
    useState<Record<string, boolean>>(getInitialExpanded());

  const toggleSection = (section: string) => {
    setExpanded((prev) => {
      const newExpanded = {
        ...prev,
        [section]: !prev[section],
      };
      // Сохраняем состояние в localStorage
      localStorage.setItem(
        PARTNER_SIDEBAR_EXPANDED_KEY,
        JSON.stringify(newExpanded)
      );
      return newExpanded;
    });
  };

  return (
    <List
      disablePadding
      sx={{
        overflowY: "auto",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {items.map((item, index) => (
        <Fragment key={index}>
          <ListItem disablePadding>
            <ListItemButton
              disableRipple
              sx={{
                py: 1.5,
                px: 2,
                "&.Mui-selected": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.1),
                  ".MuiListItemIcon-root": {
                    color: "primary.main",
                  },
                  ".MuiListItemText-root": {
                    color: "primary.main",
                  },
                },
              }}
              component={item.to ? Link : ListItemButton}
              to={item.to}
              selected={!!item.to && isPathActive(item.to, location.pathname)}
              onClick={() => item.section && toggleSection(item.section)}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <item.icon fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText primary={t(item.textKey)} />
              {item.to && badge(badges[item.to])}
              {/* Свёрнутый раздел прячет пункты: сумма видна у заголовка. */}
              {item.section &&
                !expanded[item.section] &&
                badge(
                  item.items.reduce(
                    (total, subItem) => total + (badges[subItem.to] ?? 0),
                    0
                  )
                )}
              {item.section &&
                item.items.length > 0 &&
                (expanded[item.section] ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                ))}
            </ListItemButton>
          </ListItem>
          {item.section && item.items?.length > 0 && (
            <Collapse
              in={expanded[item.section]}
              timeout="auto"
              unmountOnExit
              sx={{
                position: "relative",
              }}
            >
              <List disablePadding>
                {item.items.map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={item.to}
                      selected={isPathActive(item.to, location.pathname)}
                      disableRipple
                      dense
                      sx={{
                        py: 1.5,
                        pl: 4,
                        pr: 2,
                        "&.Mui-selected": {
                          backgroundColor: (theme) =>
                            alpha(theme.palette.primary.main, 0.1),
                          ".MuiListItemIcon-root": {
                            color: "primary.main",
                          },
                          ".MuiListItemText-root": {
                            color: "primary.main",
                          },
                        },
                      }}
                    >
                      {item.icon && (
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <item.icon fontSize="small" />
                        </ListItemIcon>
                      )}
                      <ListItemText primary={t(item.textKey)} />
                      {badge(badges[item.to])}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "1rem",
                  bottom: 0,
                  borderColor: "inherit",
                  opacity: 0.7,
                }}
              />
            </Collapse>
          )}
        </Fragment>
      ))}
    </List>
  );
};
